import React, { useEffect, useState } from 'react';
import {
    Button,
    FormControlLabel,
    InputBase,
    makeStyles,
    Switch,
    Typography,
} from '@material-ui/core';
import ControlCameraIcon from '@material-ui/icons/ControlCamera';
import CropSquareIcon from '@material-ui/icons/CropSquare';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import GestureIcon from '@material-ui/icons/Gesture';
import TimelineIcon from '@material-ui/icons/Timeline';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import { PopupConfirmation } from 'components/shared';
import { isTextEntryTarget } from './MapEditorCanvas';
import { DrawAction, DrawSession, DrawTool } from '../drawSession';
import { Point, SizeMeters, snapMeters } from '../mapGeometry';
import { OUTLINE_MAX_CORNERS, OutlineError, outlineStats, validateOutline } from '../roomOutline';

const MAGENTA = theme.palette.primary.main;
const MAGENTA_LIGHT = 'rgba(153, 86, 126, 0.08)';

const TOOLS: { tool: DrawTool; labelKey: string; icon: React.ReactElement }[] = [
    {
        tool: 'polyline',
        labelKey: 'tableMaps.wizard.draw.toolPolyline',
        icon: <TimelineIcon fontSize="small" />,
    },
    {
        tool: 'rectangle',
        labelKey: 'tableMaps.wizard.draw.toolRectangle',
        icon: <CropSquareIcon fontSize="small" />,
    },
    {
        tool: 'freehand',
        labelKey: 'tableMaps.wizard.draw.toolFreehand',
        icon: <GestureIcon fontSize="small" />,
    },
    {
        tool: 'edit',
        labelKey: 'tableMaps.wizard.draw.toolEdit',
        icon: <ControlCameraIcon fontSize="small" />,
    },
];

const ERROR_KEY: Record<OutlineError, string> = {
    tooFewCorners: 'tableMaps.wizard.draw.errorTooFewCorners',
    tooManyCorners: 'tableMaps.wizard.draw.errorTooManyCorners',
    notClosed: 'tableMaps.wizard.draw.errorNotClosed',
    tooSmall: 'tableMaps.wizard.draw.errorTooSmall',
    outOfBounds: 'tableMaps.wizard.draw.errorOutOfBounds',
    selfIntersecting: 'tableMaps.wizard.draw.errorSelfIntersecting',
};

const useStyles = makeStyles({
    root: {
        width: 272,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        paddingRight: 24,
        boxSizing: 'border-box' as const,
    },
    heading: {
        fontSize: 13,
        fontWeight: 600,
        color: MAGENTA,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
    },
    groupLabel: {
        fontSize: 11,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        letterSpacing: 0.6,
        textTransform: 'uppercase' as const,
        marginBottom: 6,
    },
    toolGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 8,
    },
    toolButton: {
        justifyContent: 'flex-start',
        height: 36,
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'none' as const,
        border: `1px solid ${theme.palette.grey['300']}`,
        color: theme.palette.text.primary,
    },
    toolButtonActive: {
        borderColor: MAGENTA,
        backgroundColor: MAGENTA_LIGHT,
        color: MAGENTA,
    },
    snapRow: {
        marginLeft: -6,
        marginBottom: 4,
    },
    snapTitle: {
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1.3,
        color: theme.palette.text.primary,
    },
    snapStatus: {
        fontSize: 11,
        color: theme.palette.text.secondary,
    },
    snapStatusValue: {
        fontWeight: 600,
        marginRight: 8,
    },
    snapStatusOn: {
        color: MAGENTA,
    },
    cornersTable: {
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 4,
        maxHeight: 200,
        overflowY: 'auto' as const,
    },
    cornerRow: {
        display: 'grid',
        gridTemplateColumns: '28px 1fr 1fr',
        alignItems: 'center',
        padding: '2px 8px',
        fontSize: 12,
    },
    cornerRowHeader: {
        color: theme.palette.text.secondary,
        fontSize: 11,
    },
    cornerRowSelected: {
        backgroundColor: MAGENTA_LIGHT,
    },
    cornerInput: {
        fontSize: 12,
        width: '90%',
    },
    emptyCorners: {
        padding: 8,
        fontSize: 12,
        color: theme.palette.text.secondary,
    },
    stats: {
        fontSize: 12,
        color: MAGENTA,
        marginTop: 6,
    },
    error: {
        fontSize: 12,
        color: theme.palette.error.main,
        marginTop: 4,
    },
    deleteButton: {
        alignSelf: 'flex-start',
        color: theme.palette.error.main,
        textTransform: 'none' as const,
        fontSize: 12,
        fontWeight: 600,
    },
    actions: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 8,
    },
    discardButton: {
        color: MAGENTA,
        borderColor: MAGENTA,
        fontWeight: 600,
    },
    acceptButton: {
        fontWeight: 600,
    },
});

interface CornerInputProps {
    value: number;
    max: number;
    onCommit: (value: number) => void;
}

/** Commits on blur / Enter so one typed number is one undo step; an out-of-map value reverts. */
function CornerInput({ value, max, onCommit }: CornerInputProps) {
    const classes = useStyles();
    const [text, setText] = useState(value.toFixed(2));

    useEffect(() => {
        setText(value.toFixed(2));
    }, [value]);

    function commit() {
        const n = Number(text);
        const isValid = text.trim() !== '' && !Number.isNaN(n) && n >= 0 && n <= max;
        if (!isValid) {
            setText(value.toFixed(2));
            return;
        }
        const rounded = snapMeters(n);
        setText(rounded.toFixed(2));
        if (rounded !== value) onCommit(rounded);
    }

    return (
        <InputBase
            className={classes.cornerInput}
            type="number"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            inputProps={{ min: 0, max, step: 0.01 }}
        />
    );
}

type PendingConfirm = { kind: 'replace'; tool: DrawTool } | { kind: 'discard' };

interface RoomDrawPanelProps {
    title: string;
    session: DrawSession;
    dispatch: React.Dispatch<DrawAction>;
    mapSize: SizeMeters;
    onDiscard: () => void;
    onAccept: () => void;
}

export default function RoomDrawPanel({
    title,
    session,
    dispatch,
    mapSize,
    onDiscard,
    onAccept,
}: RoomDrawPanelProps) {
    const classes = useStyles();
    const { outline, tool, selection, snapToGrid, snapToAngle } = session;
    const { corners, closed } = outline;
    const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
    const hasProgress = session.past.length > 0;

    const error = validateOutline(corners, closed, mapSize);
    const stats = outlineStats(corners, closed);

    useEffect(() => {
        function handleKeyDown(e: KeyboardEvent) {
            const isDialogOpen = document.querySelector('[role="dialog"]') !== null;
            if (isDialogOpen || isTextEntryTarget(e.target)) return;
            const isModified = e.ctrlKey || e.metaKey;
            const key = e.key.toLowerCase();
            if (e.key === 'Escape') {
                if (hasProgress) setPendingConfirm({ kind: 'discard' });
                else onDiscard();
            } else if (isModified && (key === 'y' || (key === 'z' && e.shiftKey))) {
                e.preventDefault();
                dispatch({ type: 'REDO' });
            } else if (isModified && key === 'z') {
                e.preventDefault();
                dispatch({ type: 'UNDO' });
            } else if ((e.key === 'Delete' || e.key === 'Backspace') && selection) {
                e.preventDefault();
                dispatch({ type: 'DELETE_SELECTION' });
            }
        }
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [hasProgress, selection, onDiscard, dispatch]);

    function handleToolSelect(next: DrawTool) {
        if (next === tool) return;
        const continuesChain = next === 'polyline' && !closed;
        if (next !== 'edit' && corners.length > 0 && !continuesChain) {
            setPendingConfirm({ kind: 'replace', tool: next });
            return;
        }
        dispatch({ type: 'SET_TOOL', payload: next });
    }

    function handleConfirmed() {
        if (pendingConfirm?.kind === 'discard') {
            onDiscard();
            return;
        }
        if (pendingConfirm?.kind !== 'replace') return;
        dispatch({ type: 'REPLACE_OUTLINE', payload: { corners: [], closed: false } });
        dispatch({ type: 'SET_TOOL', payload: pendingConfirm.tool });
    }

    const confirmCopy =
        pendingConfirm?.kind === 'discard'
            ? {
                  title: i18n.t('tableMaps.wizard.draw.discardDrawingTitle'),
                  description: i18n.t('tableMaps.wizard.draw.discardDrawingDescription'),
                  confirmLabel: i18n.t('tableMaps.wizard.draw.discard'),
              }
            : {
                  title: i18n.t('tableMaps.wizard.draw.replaceTitle'),
                  description: i18n.t('tableMaps.wizard.draw.replaceDescription'),
                  confirmLabel: i18n.t('tableMaps.wizard.draw.replaceConfirm'),
              };

    const snapSwitches = [
        {
            key: 'grid',
            checked: snapToGrid,
            titleKey: 'tableMaps.wizard.draw.snapToGrid',
            hintKey: 'tableMaps.wizard.draw.snapToGridHint',
            toggle: (on: boolean) =>
                dispatch({ type: 'SET_SNAPPING', payload: { snapToGrid: on } }),
        },
        {
            key: 'angle',
            checked: snapToAngle,
            titleKey: 'tableMaps.wizard.draw.snapToAngle',
            hintKey: snapToAngle
                ? 'tableMaps.wizard.draw.snapToAngleHintOn'
                : 'tableMaps.wizard.draw.snapToAngleHintOff',
            toggle: (on: boolean) =>
                dispatch({ type: 'SET_SNAPPING', payload: { snapToAngle: on } }),
        },
    ];

    function handleCornerCommit(index: number, patch: Partial<Point>) {
        dispatch({
            type: 'MOVE_CORNER',
            payload: { index, point: { ...corners[index], ...patch } },
        });
    }

    return (
        <div className={classes.root}>
            <Typography className={classes.heading}>{title}</Typography>

            <div>
                <Typography className={classes.groupLabel}>
                    {i18n.t('tableMaps.wizard.draw.tools')}
                </Typography>
                <div className={classes.toolGrid}>
                    {TOOLS.map(({ tool: t, labelKey, icon }) => (
                        <Button
                            key={t}
                            className={`${classes.toolButton} ${
                                tool === t ? classes.toolButtonActive : ''
                            }`}
                            startIcon={icon}
                            onClick={() => handleToolSelect(t)}
                        >
                            {i18n.t(labelKey)}
                        </Button>
                    ))}
                </div>
            </div>

            <div>
                <Typography className={classes.groupLabel}>
                    {i18n.t('tableMaps.wizard.draw.snapping')}
                </Typography>
                {snapSwitches.map(({ key, checked, titleKey, hintKey, toggle }) => (
                    <FormControlLabel
                        key={key}
                        className={classes.snapRow}
                        control={
                            <Switch
                                color="primary"
                                checked={checked}
                                onChange={(e) => toggle(e.target.checked)}
                            />
                        }
                        label={
                            <div>
                                <Typography className={classes.snapTitle}>
                                    {i18n.t(titleKey)}
                                </Typography>
                                <Typography className={classes.snapStatus}>
                                    <span
                                        className={`${classes.snapStatusValue} ${
                                            checked ? classes.snapStatusOn : ''
                                        }`}
                                    >
                                        {checked
                                            ? i18n.t('tableMaps.wizard.on')
                                            : i18n.t('tableMaps.wizard.off')}
                                    </span>
                                    {i18n.t(hintKey)}
                                </Typography>
                            </div>
                        }
                    />
                ))}
            </div>

            <div>
                <Typography className={classes.groupLabel}>
                    {i18n.t('tableMaps.wizard.draw.corners')}
                </Typography>
                <div className={classes.cornersTable}>
                    <div className={`${classes.cornerRow} ${classes.cornerRowHeader}`}>
                        <span>#</span>
                        <span>{i18n.t('tableMaps.wizard.draw.cornerX')}</span>
                        <span>{i18n.t('tableMaps.wizard.draw.cornerY')}</span>
                    </div>
                    {corners.length === 0 && (
                        <Typography className={classes.emptyCorners}>
                            {i18n.t('tableMaps.wizard.draw.noCorners')}
                        </Typography>
                    )}
                    {corners.map((corner, i) => (
                        <div
                            key={i}
                            className={`${classes.cornerRow} ${
                                selection?.kind === 'vertex' && selection.index === i
                                    ? classes.cornerRowSelected
                                    : ''
                            }`}
                        >
                            <span>{i + 1}</span>
                            <CornerInput
                                value={corner.x}
                                max={mapSize.width}
                                onCommit={(x) => handleCornerCommit(i, { x })}
                            />
                            <CornerInput
                                value={corner.y}
                                max={mapSize.height}
                                onCommit={(y) => handleCornerCommit(i, { y })}
                            />
                        </div>
                    ))}
                </div>
                <Typography className={classes.stats}>
                    {i18n.t('tableMaps.wizard.draw.stats', {
                        perimeter: stats.perimeter.toFixed(2),
                        area: stats.area.toFixed(2),
                    })}
                </Typography>
                {corners.length > 0 && error && (
                    <Typography className={classes.error}>
                        {i18n.t(ERROR_KEY[error], { max: OUTLINE_MAX_CORNERS })}
                    </Typography>
                )}
            </div>

            {tool === 'edit' && selection && (
                <Button
                    className={classes.deleteButton}
                    startIcon={<DeleteOutlineIcon fontSize="small" />}
                    onClick={() => dispatch({ type: 'DELETE_SELECTION' })}
                >
                    {selection.kind === 'vertex'
                        ? i18n.t('tableMaps.wizard.draw.deletePoint')
                        : i18n.t('tableMaps.wizard.draw.deleteSegment')}
                </Button>
            )}

            <div className={classes.actions}>
                <Button variant="outlined" className={classes.discardButton} onClick={onDiscard}>
                    {i18n.t('tableMaps.wizard.draw.discard')}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    className={classes.acceptButton}
                    disabled={error !== null}
                    onClick={onAccept}
                >
                    {i18n.t('tableMaps.wizard.draw.useShape')}
                </Button>
            </div>

            <PopupConfirmation
                open={pendingConfirm !== null}
                close={() => setPendingConfirm(null)}
                title={confirmCopy.title}
                description={confirmCopy.description}
                activeBtn={confirmCopy.confirmLabel}
                action={handleConfirmed}
            />
        </div>
    );
}
